import { useState } from "react";

export default function ContactForm({ source = "Generel kontakt" }) {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic client-side validation
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus({ submitting: false, success: false, error: "Venligst udfyld alle påkrævede felter." });
      return;
    }

    setStatus({ submitting: true, success: false, error: null });

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          source: source,
        }),
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        setStatus({
          submitting: false,
          success: false,
          error: errData.message || "Der skete en fejl under afsendelsen. Prøv igen eller ring til os direkte.",
        });
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: "Netværksfejl. Kontroller din internetforbindelse og prøv igen.",
      });
    }
  };

  return (
    <div className="rounded-none bg-white p-6 shadow-lg border border-concrete-100 md:p-8">
      {status.success ? (
        <div className="text-center py-8">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <i className="fa-solid fa-circle-check text-3xl"></i>
          </div>
          <h3 className="text-2xl font-bold text-brand-blue-900 mb-2">Tak for din henvendelse!</h3>
          <p className="text-concrete-600 mb-6">
            Vi har modtaget din besked og Simon Babiel vil vende tilbage to dig hurtigst muligt, typisk inden for 24 timer.
          </p>
          <button
            onClick={() => setStatus((prev) => ({ ...prev, success: false }))}
            className="inline-flex items-center justify-center rounded-full bg-brand-blue-800 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-blue-700 focus:outline-none"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <h3 className="text-xl font-bold text-brand-blue-900">Få et uforpligtende tilbud</h3>
          <p className="text-sm text-concrete-600">
            Udfyld formularen, så kontakter vi dig til en uforpligtende snak om dit gulvprojekt.
          </p>

          {status.error && (
            <div className="rounded-none bg-rose-50 p-4 text-sm text-rose-700 border border-rose-100 flex items-start gap-3">
              <i className="fa-solid fa-circle-exclamation mt-0.5"></i>
              <span>{status.error}</span>
            </div>
          )}

          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute -z-50 overflow-hidden h-0 w-0" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this field</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-brand-blue-900 mb-1.5">
              Navn <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-concrete-400">
                <i className="fa-solid fa-user text-sm"></i>
              </span>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className="block w-full rounded-none border border-concrete-200 bg-concrete-50 py-3 pl-10 pr-4 text-sm text-brand-blue-950 placeholder-concrete-400 transition-colors focus:border-brand-blue-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-blue-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-brand-blue-900 mb-1.5">
                Telefonnummer <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-concrete-400">
                  <i className="fa-solid fa-phone text-sm"></i>
                </span>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Tlf. nummer"
                  className="block w-full rounded-none border border-concrete-200 bg-concrete-50 py-3 pl-10 pr-4 text-sm text-brand-blue-950 placeholder-concrete-400 transition-colors focus:border-brand-blue-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-blue-600"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-brand-blue-900 mb-1.5">
                E-mailadresse <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-concrete-400">
                  <i className="fa-solid fa-envelope text-sm"></i>
                </span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="din@mail.dk"
                  className="block w-full rounded-none border border-concrete-200 bg-concrete-50 py-3 pl-10 pr-4 text-sm text-brand-blue-950 placeholder-concrete-400 transition-colors focus:border-brand-blue-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-blue-600"
                />
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-brand-blue-900 mb-1.5">
              Beskrivelse af opgaven <span className="text-rose-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="4"
              value={formData.description}
              onChange={handleChange}
              placeholder="Beskriv dit projekt (f.eks. gulvtype, ca. antal kvadratmeter, nuværende underlag osv.)..."
              className="block w-full rounded-none border border-concrete-200 bg-concrete-50 px-4 py-3 text-sm text-brand-blue-950 placeholder-concrete-400 transition-colors focus:border-brand-blue-600 focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-blue-600"
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={status.submitting}
            className="w-full inline-flex items-center justify-center rounded-full bg-brand-blue-900 px-6 py-4 text-sm font-bold text-white shadow-md transition-all hover:bg-brand-blue-800 focus:outline-none focus:ring-2 focus:ring-brand-blue-600 focus:ring-offset-2 disabled:opacity-70 cursor-pointer"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-spinner animate-spin mr-2"></i>
                Sender forespørgsel...
              </>
            ) : (
              <>
                Få et uforpligtende tilbud
                <i className="fa-solid fa-arrow-right ml-2"></i>
              </>
            )}
          </button>

          <div className="flex items-center justify-center gap-2 text-xs text-concrete-500 mt-3">
            <i className="fa-solid fa-lock"></i>
            <span>Dine oplysninger behandles fortroligt og sikkert.</span>
          </div>
        </form>
      )}
    </div>
  );
}
